import { Component, EventEmitter, HostListener, OnDestroy, OnInit, Output } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import { PedidoService } from '../../../../services/pedidos/pedido.service';
import { ImportarPedidoParaVendaService } from '../../../../services/pedidos/importar-pedido-para-venda.service';
import { PedidoDTO, PedidoParaFaturarDTO } from '../../../../models/pedidos/pedido.model';
import { EstadoDePedido, ESTADO_PEDIDO_CONFIG } from '../../../../enums/EstadoDePedido.enum';
import { resolverTipoEntrega, TipoDeEntrega, TIPO_ENTREGA_CONFIG } from '../../../../enums/TipoDeEntrega.enum';

/** Opção do filtro de tipo de serviço. `null` é a opção "Todos". */
interface OpcaoTipoServico {
  valor: TipoDeEntrega | null;
  rotulo: string;
}

/**
 * Popup que traz um pedido para o carrinho da venda.
 *
 * Faturar um pedido é uma venda — o caminho já existia saindo da tela de Pedidos; este é o
 * inverso, para quem já está no PDV. Só lista pedido com saldo a faturar (o parcialmente faturado
 * entra, o totalmente faturado não), e o filtro roda no banco para a paginação não mentir.
 */
@Component({
  selector: 'app-importar-pedido-dialog',
  templateUrl: './importar-pedido-dialog.component.html',
  styleUrls: ['./importar-pedido-dialog.component.scss'],
})
export class ImportarPedidoDialogComponent implements OnInit, OnDestroy {

  /** Emite ao fechar sem importar. */
  @Output() fechado = new EventEmitter<void>();

  /** Emite o pedido completo depois de aplicado no carrinho — o PDV recarrega a partir do cache. */
  @Output() importado = new EventEmitter<PedidoDTO>();

  visivel = true;

  pedidos: PedidoParaFaturarDTO[] = [];
  pedidoSelecionado: PedidoParaFaturarDTO | null = null;

  carregando = false;
  importando = false;

  first = 0;
  rows = 10;
  totalRecords = 0;
  opcoesLinhasPorPagina = [10, 20, 50];

  busca = '';
  tipoSelecionado: TipoDeEntrega | null = null;
  estadoSelecionado: EstadoDePedido | null = null;

  opcoesTipoServico: OpcaoTipoServico[] = [];
  opcoesEstado: { label: string; value: EstadoDePedido | null }[] = [];

  /**
   * Estados em que ainda faz sentido faturar. É o mesmo conjunto que o backend assume quando o
   * filtro vem vazio; FINALIZADO e CANCELADO ficam de fora.
   */
  private readonly estadosFaturaveis = [
    EstadoDePedido.ABERTO,
    EstadoDePedido.EM_PRODUCAO,
    EstadoDePedido.A_CAMINHO,
    EstadoDePedido.AGUARDANDO_RETIRADA,
    EstadoDePedido.ENTREGUE,
  ];

  private buscaSubject = new Subject<void>();

  constructor(
    private pedidoService: PedidoService,
    private importarPedidoService: ImportarPedidoParaVendaService,
    private messageService: MessageService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.montarOpcoesDeFiltro();
    // Espera o operador parar de digitar: cada tecla dispararia uma busca no servidor.
    this.buscaSubject
      .pipe(debounceTime(350), distinctUntilChanged())
      .subscribe(() => {
        this.first = 0;
        this.carregar();
      });
    this.carregar();
  }

  ngOnDestroy(): void {
    this.buscaSubject.complete();
  }

  // =========================================================================
  // Filtros
  // =========================================================================

  private montarOpcoesDeFiltro(): void {
    this.opcoesTipoServico = [
      { valor: null, rotulo: this.translate.instant('pedidos.tipo_entrega.TODOS') },
      ...Object.values(TipoDeEntrega).map(tipo => ({
        valor: tipo,
        rotulo: this.translate.instant('pedidos.tipo_entrega.' + tipo),
      })),
    ];

    this.opcoesEstado = [
      { label: this.translate.instant('pedidos.labels.todos_estados'), value: null },
      ...this.estadosFaturaveis.map(estado => ({
        label: this.translate.instant('pedidos.estados.' + estado),
        value: estado,
      })),
    ];
  }

  selecionarTipo(tipo: TipoDeEntrega | null): void {
    this.tipoSelecionado = tipo;
    this.first = 0;
    this.carregar();
  }

  onEstadoChange(): void {
    this.first = 0;
    this.carregar();
  }

  onBuscaChange(): void {
    this.buscaSubject.next();
  }

  // =========================================================================
  // Carga
  // =========================================================================

  private carregar(): void {
    this.carregando = true;
    this.pedidoSelecionado = null;

    this.pedidoService.listarParaFaturar({
      page: this.first / this.rows,
      size: this.rows,
      // undefined (e nao null): o buildQueryParams so descarta undefined.
      tipoEntrega: this.tipoSelecionado ?? undefined,
      estadoDePedido: this.estadoSelecionado ?? this.estadosFaturaveis.join(','),
      busca: this.busca?.trim() || undefined,
    }).subscribe({
      next: (resposta) => {
        this.pedidos = resposta?.content ?? [];
        this.totalRecords = resposta?.totalElements ?? 0;
        this.carregando = false;
      },
      // Erro (inclusive 404) cai em lista vazia: o popup nunca quebra na cara do operador.
      error: () => {
        this.pedidos = [];
        this.totalRecords = 0;
        this.carregando = false;
      },
    });
  }

  onPageChange(evento: any): void {
    this.first = evento.first ?? 0;
    this.rows = evento.rows ?? this.rows;
    this.carregar();
  }

  onRowsPerPageChange(): void {
    this.first = 0;
    this.carregar();
  }

  // =========================================================================
  // Seleção e importação
  // =========================================================================

  selecionar(pedido: PedidoParaFaturarDTO): void {
    this.pedidoSelecionado = pedido;
  }

  /**
   * Busca o pedido completo e monta o carrinho.
   *
   * A lista não traz os itens de propósito; eles vêm aqui, no clique — o que também garante o
   * saldo atual, caso outro caixa tenha faturado parte do pedido nesse meio-tempo.
   */
  importar(): void {
    if (!this.pedidoSelecionado?.id || this.importando) return;
    this.importando = true;

    this.pedidoService.getById(this.pedidoSelecionado.id).subscribe({
      next: (pedido) => {
        const itensComSaldo = this.importarPedidoService.itensComSaldo(pedido.itens || []);
        const importou = this.importarPedidoService.aplicarNoCarrinho(pedido, itensComSaldo);
        this.importando = false;

        if (!importou) {
          this.messageService.add({
            severity: 'warn',
            summary: this.translate.instant('common.atencao'),
            detail: this.translate.instant('pedidos.mensagens.sem_itens_faturar'),
          });
          return;
        }

        this.visivel = false;
        this.importado.emit(pedido);
      },
      error: () => {
        this.importando = false;
        this.messageService.add({
          severity: 'error',
          summary: this.translate.instant('common.erro'),
          detail: this.translate.instant('vendas.importar_pedido.erro_importar'),
        });
      },
    });
  }

  /**
   * Enter confirma a importacao do pedido selecionado. O Esc e tratado pela tela de venda, junto
   * com os demais dialogs dela.
   */
  @HostListener('document:keydown.enter', ['$event'])
  onEnter(evento: KeyboardEvent): void {
    if (!this.visivel || !this.pedidoSelecionado || this.importando) return;
    evento.preventDefault();
    this.importar();
  }

  fechar(): void {
    this.visivel = false;
    this.fechado.emit();
  }

  // =========================================================================
  // Apresentação
  // =========================================================================

  rotuloTipoServico(pedido: PedidoParaFaturarDTO): string {
    const tipo = resolverTipoEntrega(pedido?.tipoEntrega);
    return tipo ? this.translate.instant('pedidos.tipo_entrega.' + tipo) : '';
  }

  iconeTipoServico(pedido: PedidoParaFaturarDTO): string {
    const tipo = resolverTipoEntrega(pedido?.tipoEntrega);
    return tipo ? TIPO_ENTREGA_CONFIG[tipo].icone : 'pi pi-tag';
  }

  rotuloEstado(pedido: PedidoParaFaturarDTO): string {
    const estado = this.resolverEstado(pedido);
    return estado ? this.translate.instant('pedidos.estados.' + estado) : '';
  }

  corEstado(pedido: PedidoParaFaturarDTO): string {
    const estado = this.resolverEstado(pedido);
    return estado ? ESTADO_PEDIDO_CONFIG[estado]?.cor ?? '' : '';
  }

  /** Mesa ou comanda, quando o pedido é de consumo no local. */
  identificacaoDeMesa(pedido: PedidoParaFaturarDTO): string {
    const partes: string[] = [];
    if (pedido?.numeroMesa != null) {
      partes.push(`${this.translate.instant('pedidos.labels.mesa')} ${pedido.numeroMesa}`);
    }
    if (pedido?.numeroComanda != null) {
      partes.push(`${this.translate.instant('pedidos.labels.comanda')} ${pedido.numeroComanda}`);
    }
    return partes.join(' · ');
  }

  /** O pedido já foi faturado em parte quando ainda falta menos do que o total. */
  ehFaturadoParcial(pedido: PedidoParaFaturarDTO): boolean {
    return (pedido?.valorPendente ?? 0) > 0
      && (pedido?.valorTotal ?? 0) > (pedido?.valorPendente ?? 0);
  }

  private resolverEstado(pedido: PedidoParaFaturarDTO): EstadoDePedido | null {
    const estado = pedido?.estadoDePedido;
    if (!estado) return null;
    const valor = typeof estado === 'string' ? estado : (estado.enum || '');
    return Object.values(EstadoDePedido).includes(valor as EstadoDePedido)
      ? (valor as EstadoDePedido)
      : null;
  }
}
